import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, User } from 'lucide-react';
import { toast } from 'sonner';

const PartnerRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    business_name: '',
    partner_type: 'contractor'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Partner registration submitted successfully!');
        navigate('/');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] p-8" data-testid="partner-register-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to Home</span>
          </button>
          <h1 className="text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Partner Registration</h1>
          <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Join our network of logistics partners</p>
        </div>

        {/* Form Card */}
        <div className="card-glass p-8" data-testid="partner-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Partner Type</label>
              <select
                className="input-base w-full"
                value={formData.partner_type}
                onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
                data-testid="partner-type-select"
              >
                <option value="contractor">Independent Contractor</option>
                <option value="company">Logistics Company</option>
              </select>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  required
                  className="input-base w-full pl-11"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="full-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Business Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  required
                  className="input-base w-full pl-11"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="ABC Logistics"
                  data-testid="business-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  required
                  className="input-base w-full pl-11"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="partner@example.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2" style={{ fontFamily: 'Manrope' }}>Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 w-5 h-5 text-white/40" />
                <input
                  type="tel"
                  required
                  className="input-base w-full pl-11"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-[#FFD60A]/10 border border-[#FFD60A]/30">
            <p className="text-white/80 text-sm" style={{ fontFamily: 'Manrope' }}>
              <strong className="text-[#FFD60A]">Note:</strong> Your application will be reviewed by our team. You'll receive a response within 2-3 business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegister;